"use client";

import { Avatar, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, styled, Tooltip } from "@mui/material";
import { MouseEvent, useState } from "react";
import { AccountCircle, Construction, Logout } from "@mui/icons-material";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Flip from "@/app/features/shared/layout/flip";
import NextLink from 'next/link';
import { disableDraftMode } from "@/app/lib/actions/editing";
import { ADMIN_NAVIGATION_ITEMS } from "@/app/features/sites/admin/navigation/admin-navigation";
import IconByName from "@/app/features/sites/admin/navigation/ui/icon-by-name";
import { hasAdminRights } from "@/app/lib/auth/roles";


const StyledAvatar = styled(Avatar)(({ theme }) => {
    return {
        height: '52px',
        width: '52px',
        aspectRatio: '1/1',
        borderWidth: 2,
        borderColor: theme.palette.primary.main,
    }
});

interface UserAvatarProps {
    user: {
        name?: string | null
        email?: string | null
        image?: string | null
        roles?: string[]
    }
}

export default function AvatarMenu({ user }: UserAvatarProps) {
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuAnchor);
    const isFrontendAdmin = hasAdminRights(user.roles);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setMenuAnchor((current) => current ? null : event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleLogout = async () => {
        try {
            await disableDraftMode();
        } catch (e) {
            console.error('disableDraftMode failed', e);
        } finally {
            await signOut({ callbackUrl: '/' });
            handleMenuClose();
        }
    }

    return (
        <>
            <Flip
                isFlipped={true}
                frontView={
                    <Image
                        src='/assets/logo/logo_small_icon_only.webp'
                        alt='Logo museum erding'
                        style={{
                            padding: '8px',
                            height: '100%',
                            width: '100%',
                        }}
                        width={300}
                        height={300}
                    />
                }
                backView={
                    <Tooltip title="Administrationsmenü">
                        <IconButton
                            id="account-menu-button"
                            onClick={handleMenuClick}
                            className="aspect-square"
                            aria-label={isMenuOpen ? "Administrationsmenü schließen" : "Administrationsmenü öffnen"}
                            aria-controls={isMenuOpen ? 'account-menu' : undefined}
                            aria-haspopup="menu"
                            aria-expanded={isMenuOpen ? 'true' : undefined}
                            style={{
                                height: '100%',
                                width: '100%',
                                padding: '2px',
                            }}
                        >
                            <StyledAvatar alt={user.name || "Angemeldetes Benutzerkonto"} src={user.image || undefined}/>
                        </IconButton>
                    </Tooltip>
                }
            />
            <Menu
                    anchorEl={menuAnchor}
                    id="account-menu"
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    MenuListProps={{
                        'aria-labelledby': 'account-menu-button',
                    }}
                    slotProps={{
                        paper: {
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            }
                        }
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                >
                    <MenuItem component={NextLink} href="/admin" prefetch={false}>
                        <ListItemIcon><Construction /></ListItemIcon>
                        <ListItemText>Dashboard</ListItemText>
                    </MenuItem>
                    <Divider/>
                    {ADMIN_NAVIGATION_ITEMS.map((item) => (
                        <MenuItem
                            key={item.route}
                            component={NextLink}
                            href={item.route}
                            prefetch={false}
                        >
                            <ListItemIcon><IconByName name={item.iconName} color="currentColor" /></ListItemIcon>
                            <ListItemText>{item.title}</ListItemText>
                        </MenuItem>
                    ))}
                    <Divider/>
                    <MenuItem component={NextLink} href="/admin/account" prefetch={false}>
                        <ListItemIcon><AccountCircle /></ListItemIcon>
                        <ListItemText>Mein Konto</ListItemText>
                    </MenuItem>
                    {isFrontendAdmin && <MenuItem component={NextLink} href="/admin/users" prefetch={false}>
                        <ListItemIcon><IconByName name="ManageAccounts" color="currentColor" /></ListItemIcon>
                        <ListItemText>Benutzer/Rollen</ListItemText>
                    </MenuItem>}
                    <Divider/>
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon><Logout /></ListItemIcon>
                        <ListItemText>Abmelden</ListItemText>
                    </MenuItem>
                </Menu>
        </>
    )
}
